import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

interface WastewaterGHGChartProps {
  location: string;
  years: number[];
  values: Array<number | null>;
  storedYear?: number | null;
  embedOnly?: boolean;
}

const WastewaterGHGChart: React.FC<WastewaterGHGChartProps> = ({
  location,
  years,
  values,
  storedYear = null,
  embedOnly = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const buildChart = useCallback(() => {
    if (!canvasRef.current) return;
    try {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const existing = Chart.getChart(canvasRef.current);
      if (existing) existing.destroy();
    } catch {
      /* ignore */
    }

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const pointRadius = years.map((year) => (year === storedYear ? 5 : 2));
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets: [
          {
            label: 'Wastewater GHG (MT CO₂e)',
            data: values,
            borderColor: '#0f766e',
            backgroundColor: 'rgba(15, 118, 110, 0.12)',
            tension: 0.2,
            pointRadius,
            borderWidth: 2,
            spanGaps: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' },
          title: {
            display: true,
            text: `Wastewater GHG Emissions (${location})`,
            font: { size: 16, weight: 'bold' },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Emissions (Metric Tons CO₂e)' },
          },
          x: { title: { display: true, text: 'Year' } },
        },
        interaction: { mode: 'index', intersect: false },
      },
    });
  }, [location, years, values, storedYear]);

  useEffect(() => {
    buildChart();
    const canvasNode = canvasRef.current;
    return () => {
      try {
        if (chartRef.current) chartRef.current.destroy();
        if (canvasNode) {
          const existing = Chart.getChart(canvasNode);
          if (existing) existing.destroy();
        }
      } catch {
        /* ignore */
      }
    };
  }, [buildChart]);

  return (
    <div className="chart-container" style={{ height: embedOnly ? '100%' : '400px', width: '100%' }}>
      <canvas ref={canvasRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};

export default WastewaterGHGChart;
